import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { UserPlus, Eye, EyeSlash } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function AcceptInvitationPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setError(''); setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/accept-invitation`, { token, name, password });
      setSuccess(true);
      setEmail(res.data.email || '');
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to accept invitation');
    } finally { setLoading(false); }
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-[#25293C] dark:to-[#2F3349] p-4">
        <div className="text-center">
          <p className="text-red-500 font-medium">Invalid invitation link. No token provided.</p>
          <Link to="/login" className="text-primary hover:underline text-sm mt-2 block">Go to Login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-[#25293C] dark:to-[#2F3349] p-4">
      <div className="w-full max-w-md bg-white dark:bg-[#2F3349] rounded-xl shadow-xl p-8 border border-gray-100 dark:border-[#434968]" data-testid="accept-invitation-page">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <UserPlus size={22} className="text-primary" weight="duotone" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Join RoofMate</h1>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">Complete your account setup to get started.</p>

        {success ? (
          <div className="space-y-4">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <p className="text-sm text-green-700 dark:text-green-300 font-medium">Account created successfully!</p>
              {email && <p className="text-xs text-green-600 dark:text-green-400 mt-1">You can now log in with {email}</p>}
              <p className="text-xs text-green-600 dark:text-green-400 mt-1">Redirecting to login...</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-600 dark:text-red-400">{error}</div>}
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Full Name</label>
              <Input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your full name" required data-testid="invite-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Password</label>
              <div className="relative">
                <Input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters" required data-testid="invite-password-input" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Confirm Password</label>
              <Input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Re-enter password" required data-testid="invite-confirm-input" />
            </div>
            <button type="submit" disabled={loading} className="w-full h-10 bg-primary text-white rounded-lg font-semibold text-sm hover:bg-primary/90 transition-colors disabled:opacity-50" data-testid="invite-submit-btn">
              {loading ? 'Creating Account...' : 'Create Account & Join'}
            </button>
            <p className="text-center text-xs text-gray-400">Already have an account? <Link to="/login" className="text-primary hover:underline">Login</Link></p>
          </form>
        )}
      </div>
    </div>
  );
}
